import { type ReactEventHandler, type SyntheticEvent, useEffect, useMemo, useRef } from 'react';

// COULDDO: Add support for more event types
type EventType = 'onMouseDown' | 'onClick' | 'onContextMenu';
type EventBehavior = string;
type EventData = string;
export type EventHandler = (
  event: SyntheticEvent,
  eventTarget: HTMLElement,
  eventData?: EventData,
) => void;
type EventHandlers = Record<EventBehavior, EventHandler>;
type ContainerProps<T extends EventType> = {
  [eventType in T]: ReactEventHandler;
};

const getEventTypeAttribute = (eventType: EventType) => `data-rm-event-${eventType.toLowerCase()}`;
const getEventBehaviorAttribute = (eventType: EventType) =>
  `data-rm-event-${eventType.toLowerCase()}-behavior`;
const getEventDataAttribute = (eventType: EventType) =>
  `data-rm-event-${eventType.toLowerCase()}-data`;

export const bindEventHandler = (
  eventType: EventType,
  eventBehavior: EventBehavior,
  eventData?: EventData,
) => ({
  [getEventTypeAttribute(eventType)]: '1',
  [getEventBehaviorAttribute(eventType)]: eventBehavior,
  [getEventDataAttribute(eventType)]: eventData,
});

export const buildEventHandlerCssSelector = (eventType: EventType, eventBehavior: EventBehavior) =>
  `[${getEventBehaviorAttribute(eventType)}="${eventBehavior}"][${getEventTypeAttribute(eventType)}="1"]`;

export const useEventDelegationHandler = <T extends EventType>(
  eventType: T,
  eventHandlers: EventHandlers,
): ContainerProps<T> => {
  const eventHandlerRef = useRef<ReactEventHandler | null>(null);

  useEffect(() => {
    eventHandlerRef.current = (event: SyntheticEvent) => {
      const eventTarget = (event.target as HTMLElement)?.closest(
        `[${getEventTypeAttribute(eventType)}]`,
      );

      if (!eventTarget) {
        return;
      }

      const eventBehaviorAttribute = getEventBehaviorAttribute(eventType);
      const eventBehavior = eventTarget.getAttribute(eventBehaviorAttribute);
      const eventDataAttribute = getEventDataAttribute(eventType);
      const eventHandler = eventHandlers?.[eventBehavior as EventBehavior];

      eventHandler?.(
        event,
        eventTarget as HTMLElement,
        eventTarget.getAttribute(eventDataAttribute) as EventData,
      );
    };
  }, [eventType, eventHandlers]);

  return useMemo(
    () =>
      ({
        [eventType]: (event: SyntheticEvent) => eventHandlerRef.current?.(event),
      }) as ContainerProps<T>,
    [eventType],
  );
};
